// activeAgentMonitor.js
// Service to monitor and maintain WebSocket connections for all active agents
// This ensures terminal logs are captured even when users are not on the dashboard

import { tradingAgentApi } from './api';
import globalWebSocketService from './globalWebSocketService';

class ActiveAgentMonitor {
  constructor() {
    this.monitoredAgents = new Map(); // Map of agentId -> agent info
    this.isMonitoring = false;
    this.checkInterval = null;
  }

  // Start monitoring all active agents
  async startMonitoring(userId) {
    if (this.isMonitoring) {
      console.log('[ActiveAgentMonitor] Already monitoring');
      return;
    }

    console.log('[ActiveAgentMonitor] Starting to monitor active agents');
    this.isMonitoring = true;

    // Initial check
    await this.checkActiveAgents(userId);

    // Set up periodic checks every 30 seconds
    this.checkInterval = setInterval(() => {
      this.checkActiveAgents(userId);
    }, 30000);
  }

  // Check for active agents and ensure their WebSocket connections are alive
  async checkActiveAgents(userId) {
    try {
      console.log('[ActiveAgentMonitor] Checking for active agents...');
      
      // Get all agents for the user (token filtering handled by API)
      const agents = await tradingAgentApi.getAgents();
      
      // Filter for active agents
      const activeAgents = agents.filter(agent => agent.active);
      console.log(`[ActiveAgentMonitor] Found ${activeAgents.length} active agents`);

      // Process each active agent
      for (const agent of activeAgents) {
        await this.ensureAgentConnection(agent);
      }

      // Clean up connections for agents that are no longer active
      for (const [agentId, agentInfo] of this.monitoredAgents.entries()) {
        const stillActive = activeAgents.some(a => a.id === parseInt(agentId));
        if (!stillActive) {
          console.log(`[ActiveAgentMonitor] Agent ${agentId} is no longer active, cleaning up`);
          this.stopMonitoringAgent(agentId);
        }
      }
    } catch (error) {
      console.error('[ActiveAgentMonitor] Error checking active agents:', error);
    }
  }

  // Ensure an agent has active WebSocket connections
  async ensureAgentConnection(agent) {
    const agentId = agent.id.toString();
    
    // Check if we're already monitoring this agent
    if (this.monitoredAgents.has(agentId)) {
      // Check if connection is still alive
      const connectionStatus = globalWebSocketService.getConnectionStatus(
        agent.id, 
        agent.external_id, 
        'logs'
      );
      
      if (connectionStatus && connectionStatus.connected) {
        console.log(`[ActiveAgentMonitor] Agent ${agentId} connection is alive`);
        return;
      } else {
        console.log(`[ActiveAgentMonitor] Agent ${agentId} connection is dead, reconnecting...`);
      }
    }

    // Need to start monitoring this agent
    console.log(`[ActiveAgentMonitor] Starting to monitor agent ${agentId} (${agent.name})`);
    
    try {
      // Get agent details including external_id
      let externalId = agent.external_id;
      
      if (!externalId) {
        // Fetch full agent details if external_id is missing
        const agentDetails = await tradingAgentApi.getAgentById(agent.id);
        externalId = agentDetails.external_id;
      }

      if (!externalId) {
        console.error(`[ActiveAgentMonitor] No external_id for agent ${agentId}, cannot establish connection`);
        return;
      }

      // Get active strategy if available
      let strategyId = null;
      try {
        const strategies = await tradingAgentApi.getAgentStrategies(agent.id);
        const activeStrategy = strategies.find(s => s.status === 'running' || s.status === 'active');
        if (activeStrategy) {
          strategyId = activeStrategy.id;
        }
      } catch (error) {
        console.log(`[ActiveAgentMonitor] Could not fetch strategies for agent ${agentId}:`, error);
      }

      // Start WebSocket connections for both logs and chat
      console.log(`[ActiveAgentMonitor] Starting WebSocket connections for agent ${agentId}, external_id: ${externalId}`);
      
      // Start logs connection
      globalWebSocketService.startConnection(agent.id, externalId, strategyId, 'logs');
      
      // Start chat connection
      globalWebSocketService.startConnection(agent.id, externalId, strategyId, 'chat');

      // Store in monitored agents
      this.monitoredAgents.set(agentId, {
        id: agent.id,
        name: agent.name,
        external_id: externalId,
        strategy_id: strategyId,
        started_at: new Date()
      });

      console.log(`[ActiveAgentMonitor] Successfully started monitoring agent ${agentId}`);
    } catch (error) {
      console.error(`[ActiveAgentMonitor] Error starting connection for agent ${agentId}:`, error);
    }
  }

  // Stop monitoring a specific agent
  stopMonitoringAgent(agentId) {
    const agentInfo = this.monitoredAgents.get(agentId.toString());
    if (!agentInfo) return;

    console.log(`[ActiveAgentMonitor] Stopping monitoring for agent ${agentId}`);
    
    // Stop WebSocket connections
    globalWebSocketService.stopConnection(agentInfo.id, agentInfo.external_id, 'logs');
    globalWebSocketService.stopConnection(agentInfo.id, agentInfo.external_id, 'chat');
    
    // Remove from monitored agents
    this.monitoredAgents.delete(agentId.toString());
  }

  // Stop all monitoring
  stopMonitoring() {
    if (!this.isMonitoring) return;

    console.log('[ActiveAgentMonitor] Stopping all monitoring');
    this.isMonitoring = false;

    // Clear interval
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    // Stop all agent connections
    for (const [agentId] of this.monitoredAgents.entries()) {
      this.stopMonitoringAgent(agentId);
    }

    this.monitoredAgents.clear();
  }

  // Get monitoring status
  getStatus() {
    return {
      isMonitoring: this.isMonitoring,
      monitoredAgents: Array.from(this.monitoredAgents.entries()).map(([id, info]) => ({
        id,
        name: info.name,
        external_id: info.external_id,
        started_at: info.started_at
      }))
    };
  }
}

// Create singleton instance
const activeAgentMonitor = new ActiveAgentMonitor();

export default activeAgentMonitor;